/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  forwardRef,
  useCallback,
  useMemo,
  useState,
} from 'react';
import type {
  CSSProperties,
  ComponentType,
  ForwardedRef,
  ReactElement,
  Ref,
} from 'react';

import { useTheme } from 'styled-components';
import {
  StyledButtons,
  StyledWrapper,
  StyledInput,
  ButtonWrapper,
  StatusIconWrapper,
} from './styled';

import type { InputProps } from './types';

function InputInner<
  RefElementType,
  InputComponent extends
  | keyof JSX.IntrinsicElements
  | ComponentType<any> = 'input',
>(
  props: InputProps<InputComponent>,
  ref: ForwardedRef<RefElementType>,
): ReactElement {
  const {
    component: RootComponent = 'input',
    disabled = false,
    hasError = false,
    hasWarning = false,
    className = '',
    small = false,
    isOnlyBorderBottom = false,
    leftButtons = undefined,
    rightButtons = [],
    size = 'medium',
    style,
    ...rest
  } = props;
  const { input } = useTheme();
  const itemSize = small ? 'small' : size;

  const [leftButtonsWidth, setLeftButtonsWidth] = useState(0);
  const [rightButtonsWidth, setRightButtonsWidth] = useState(0);

  const leftButtonsRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setLeftButtonsWidth(node.offsetWidth);
    }
  }, [setLeftButtonsWidth]);
  const rightButtonsRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setRightButtonsWidth(node.offsetWidth);
    }
  }, [setRightButtonsWidth]);

  const mergedRightButtons = useMemo(() => [
    ...hasError ? [() => (
      <StatusIconWrapper $isCenter={rightButtons.length === 0}>
        {input.ERROR_ICON}
      </StatusIconWrapper>
    )] : [],
    ...hasWarning ? [() => (
      <StatusIconWrapper $isCenter={rightButtons.length === 0}>
        {input.WARNING_ICON}
      </StatusIconWrapper>
    )] : [],
    ...rightButtons,
  ], [hasError, hasWarning, input, rightButtons]);

  const hasLeftButtons = leftButtons ? leftButtons.length > 0 : false;
  const hasRightButtons = mergedRightButtons.length > 0;

  const inputStyle = useMemo(() => ({
    ...style,
    paddingLeft: `${leftButtonsWidth || input.INDENT}px`,
    paddingRight: `${rightButtonsWidth || input.INDENT}px`,
  }) as CSSProperties, [input, leftButtonsWidth, rightButtonsWidth, style]);

  return (
    <StyledWrapper>
      <StyledInput
        as={RootComponent}
        {...rest}
        disabled={disabled}
        $hasError={hasError}
        $hasWarning={hasWarning}
        $isOnlyBorderBottom={isOnlyBorderBottom}
        $leftButtonsLength={leftButtons ? leftButtons.length : 0}
        $rightButtonsLength={rightButtons ? rightButtons.length : 0}
        $size={itemSize}
        className={className}
        ref={ref as ForwardedRef<any>}
        style={inputStyle}
      />

      {hasLeftButtons && (
        <StyledButtons $isLeft $size={itemSize} ref={leftButtonsRef}>
          {leftButtons?.map((renderButton, index) => (
            <ButtonWrapper key={index}>{renderButton(props, index)}</ButtonWrapper>
          ))}
        </StyledButtons>
      )}
      {hasRightButtons && (
        <StyledButtons $isRight $size={itemSize} ref={rightButtonsRef}>
          {mergedRightButtons?.map((renderButton, index) => (
            <ButtonWrapper key={index}>{renderButton(props, index)}</ButtonWrapper>
          ))}
        </StyledButtons>
      )}
    </StyledWrapper>
  );
}

export const Input = forwardRef(InputInner) as <
  RefElementType,
  InputComponent extends
  | keyof JSX.IntrinsicElements
  | ComponentType<any> = 'input',
>(
  props: InputProps<InputComponent> & {
    ref?: Ref<RefElementType>;
  }
) => ReactElement;
